/**
 * ParametroSimpleEditorDialog — Editor inline de parámetros simples.
 *
 * Componente genérico y reutilizable: edita cualquier parámetro tipo "simple"
 * (1 valor único, texto plano) del módulo Parámetros sin requerir navegar a /parametros.
 *
 * Props:
 *   - codigo            : código del parámetro a editar (ej: 'ibc_comunicado_activo')
 *   - open              : boolean — controla visibilidad del Dialog
 *   - onClose           : () => void — al cerrar
 *   - onSaved           : () => void — tras guardar con éxito (útil para refetch)
 *   - etiquetaTitulo    : label del campo "Título" (default: "Título")
 *   - etiquetaMensaje   : label del campo "Mensaje" (default: "Mensaje")
 *   - etiquetaToggle    : label del switch "Publicar" (default: "Publicar ahora")
 *   - placeholderMsg    : placeholder del textarea
 *   - renderPreview     : (data) => JSX — función que pinta la vista previa del widget
 *                         donde `data = { titulo, mensaje, publicado }`. Opcional.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { parametrosAPI } from '../utils/api';

const ParametroSimpleEditorDialog = ({
  codigo,
  open,
  onClose,
  onSaved,
  etiquetaTitulo = 'Título',
  etiquetaMensaje = 'Mensaje',
  etiquetaToggle = 'Publicar ahora',
  placeholderMsg = 'Escribe el contenido aquí…',
  renderPreview,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [publicado, setPublicado] = useState(true);

  // Snapshot inicial para detectar cambios y deshabilitar "Guardar" cuando no hay diffs
  const [original, setOriginal] = useState({ titulo: '', mensaje: '', publicado: true });

  useEffect(() => {
    if (!open || !codigo) return;
    let cancelled = false;
    setLoading(true);
    parametrosAPI.get(codigo)
      .then(res => {
        if (cancelled) return;
        const p = res.data;
        const valor = (p?.valores && p.valores[0]?.valor) || '';
        const snapshot = {
          titulo: p?.nombre || '',
          mensaje: valor,
          publicado: !!p?.activo,
        };
        setTitulo(snapshot.titulo);
        setMensaje(snapshot.mensaje);
        setPublicado(snapshot.publicado);
        setOriginal(snapshot);
      })
      .catch(err => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          toast.error(`El parámetro "${codigo}" no existe todavía. Créalo primero desde /parametros.`);
        } else {
          toast.error('No pude cargar el parámetro: ' + (err?.response?.data?.detail || err.message));
        }
        onClose?.();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, codigo, onClose]);

  const dirty = useMemo(() => (
    titulo !== original.titulo ||
    mensaje !== original.mensaje ||
    publicado !== original.publicado
  ), [titulo, mensaje, publicado, original]);

  const handleSave = async () => {
    if (!dirty || saving) return;
    if (!titulo.trim()) {
      toast.error('El título no puede estar vacío.');
      return;
    }
    setSaving(true);
    try {
      // Orden importa: primero actualizamos el CUERPO (mientras el param sigue
      // en su estado original), luego el título + estado activo. Si el admin
      // está despublicando, el valor ya quedó guardado antes de que activo=false.
      if (mensaje !== original.mensaje) {
        await parametrosAPI.updateValorSimple(codigo, mensaje);
      }
      if (titulo !== original.titulo || publicado !== original.publicado) {
        await parametrosAPI.update(codigo, { nombre: titulo.trim(), activo: publicado });
      }
      toast.success('Comunicado actualizado');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error('No pude guardar: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (next) => {
    if (!next && !saving) onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="parametro-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle>Editar parámetro</DialogTitle>
          <DialogDescription>
            Los cambios se reflejan inmediatamente en la plataforma para todos los usuarios.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="parametro-editor-loading">
            <Loader2 className="animate-spin text-[#9d8272]" size={24} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            {/* Form */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="param-titulo" className="text-sm font-medium text-[#2D2D2D] mb-1 block">
                  {etiquetaTitulo}
                </Label>
                <Input
                  id="param-titulo"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  maxLength={140}
                  data-testid="parametro-editor-titulo"
                  disabled={saving}
                />
                <p className="text-xs text-[#888] mt-1">{titulo.length}/140 caracteres</p>
              </div>

              <div>
                <Label htmlFor="param-mensaje" className="text-sm font-medium text-[#2D2D2D] mb-1 block">
                  {etiquetaMensaje}
                </Label>
                <Textarea
                  id="param-mensaje"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={7}
                  placeholder={placeholderMsg}
                  maxLength={2000}
                  className="resize-none"
                  data-testid="parametro-editor-mensaje"
                  disabled={saving}
                />
                <p className="text-xs text-[#888] mt-1">{mensaje.length}/2000 caracteres</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-[#FAF8F6] rounded-md border border-[#E8E8E7]">
                <div>
                  <Label htmlFor="param-switch" className="text-sm font-medium text-[#2D2D2D] cursor-pointer">
                    {etiquetaToggle}
                  </Label>
                  <p className="text-xs text-[#888]">
                    {publicado ? 'Visible en toda la plataforma' : 'Oculto del Home'}
                  </p>
                </div>
                <Switch
                  id="param-switch"
                  checked={publicado}
                  onCheckedChange={setPublicado}
                  data-testid="parametro-editor-switch"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#2D2D2D]">Vista previa</Label>
              <div
                className="border border-dashed border-[#E8E8E7] rounded-md p-3 bg-[#FAFAFA] min-h-[280px]"
                data-testid="parametro-editor-preview"
              >
                {renderPreview ? (
                  renderPreview({ titulo, mensaje, publicado })
                ) : (
                  <div className="text-xs text-[#888] italic">
                    (Sin vista previa configurada)
                  </div>
                )}
              </div>
              {!publicado && (
                <p className="text-xs text-amber-600">
                  Nota: con el toggle apagado, los usuarios no verán este contenido aunque hayas guardado cambios.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            disabled={saving}
            data-testid="parametro-editor-cancelar"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!dirty || saving || loading}
            className="bg-[#9d8272] hover:bg-[#876a58] text-white"
            data-testid="parametro-editor-guardar"
          >
            {saving ? (
              <><Loader2 size={16} className="animate-spin mr-2" /> Guardando…</>
            ) : (
              <><Save size={16} className="mr-2" /> Guardar cambios</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ParametroSimpleEditorDialog;
